import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { SimpleCheckList, SimpleCheckListItem } from "@components"
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid"
import { Button, Spinner } from "@heroui/react"
import PropTypes from "prop-types"

const Checklist = ({
  event,
  handleViewUnmatched,
  handleSendOutstandingEmails,
  isReadOnly,
  isSendEmailsDisabled,
  noOfEmailsSent = 0, 
  noOfEmailsNotSent = 0, 
  noOfUnmatched = 0, 
  noOfStudents = 0,
  sendingStats,
}) => {
  const navigate = useNavigate()
  const prevQueuedCountRef = useRef(sendingStats?.queued || 0)
  const [showSendSummary, setShowSendSummary] = useState(false)

  useEffect(() => {
    // Just finished sending
    if (prevQueuedCountRef.current > 0 && sendingStats.queued === 0) {
      setShowSendSummary(true)
    }

    // Sending again → hide summary
    if (sendingStats.queued > 0) {
      setShowSendSummary(false)
    }
  
    prevQueuedCountRef.current = sendingStats.queued
  }, [sendingStats.queued])

  return (
    <SimpleCheckList>
      {sendingStats.queued > 0 && sendingStats.total > 0 && (
        <SimpleCheckListItem 
          title={`${sendingStats.queued} of ${sendingStats.total} emails are queued for sending.`}
          description="Emails are being sent in the background. Sending additional emails are disabled until this has completed!"
          icon={<Spinner color="success" variant="simple" />}
        />
      )}
      
      {showSendSummary && (
        <SimpleCheckListItem
          title="Sending Complete"
          description="To view delivery stats go to the relevant record in the communications section and click view delivery stats."
          icon={<CheckCircleIcon className="w-8 h-8 text-green-500" />}
          button={<Button color="primary" onPress={() => navigate(`/event/${event.id}/communications`)} className="ml-3 min-w-52">View Details</Button>}
        />
      )}
      
      {noOfUnmatched === 0 && (
        <SimpleCheckListItem 
          title={`${noOfStudents} of ${noOfStudents} students have been matched to a guardian.`} 
          icon={<CheckCircleIcon className="w-8 h-8 text-green-500" />}
          description="All students have a guardian assigned and are available to be emailed."
        />
      )}

      {noOfUnmatched > 0 && (
        <SimpleCheckListItem 
          title={`${noOfUnmatched} of ${noOfStudents} students could not be matched to a guardian.`} 
          description="To add a guardian find the relevant student & click Actions > Add Guardian."
          icon={<XCircleIcon className="w-8 h-8 text-red-500" />}
          button={<Button color="primary" onPress={() => handleViewUnmatched()} className="ml-3 min-w-52">View Unmatched</Button>}
        />
      )}

      {noOfEmailsNotSent > 0 && (
        <SimpleCheckListItem 
          title={`${noOfEmailsNotSent} of ${noOfEmailsSent + noOfEmailsNotSent} invitation emails have not been sent.`}
          description="Unmatched students won't be emailed."
          icon={<XCircleIcon className="w-8 h-8 text-red-500" />}
          button={<Button color="primary" isDisabled={isSendEmailsDisabled || isReadOnly} onPress={() => handleSendOutstandingEmails()} className="ml-3 min-w-52">Send Outstanding Emails</Button>}
          showDivider={false}
        />
      )}

      {noOfEmailsNotSent === 0 && (
        <SimpleCheckListItem 
          title={`${noOfEmailsSent} of ${noOfEmailsSent + noOfEmailsNotSent} invitation emails have been sent.`} 
          description="You have no outstanding emails to send."
          icon={<CheckCircleIcon className="w-8 h-8 text-green-500" />}
          showDivider={false}
        />
      )}
    </SimpleCheckList>
  )
}

Checklist.propTypes = {
  event: PropTypes.object.isRequired,
  handleViewUnmatched: PropTypes.func.isRequired,
  handleSendOutstandingEmails: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  isSendEmailsDisabled: PropTypes.bool.isRequired,
  noOfEmailsSent: PropTypes.number,
  noOfEmailsNotSent: PropTypes.number,
  noOfUnmatched: PropTypes.number,
  noOfStudents: PropTypes.number,
  sendingStats: PropTypes.object.isRequired,
}

export default Checklist